import { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import Toggle from "../common/Toggle";
import QrCode from "../common/QrCode";
import {
  setHighContrast,
  setKeepAwake,
  setKeepFavoritesOffline,
  setLyricSize,
  setShowChords,
  toggleDimRoom,
} from "../../actions/preferencesActions";
import { disableSoundSync, enableSoundSync } from "../../actions/soundSyncActions";
import { rotateHostKey } from "../../actions/hostActions";
import { clearSession } from "../../actions/sessionActions";
import { showNotice } from "../../actions/noticeActions";
import { failureMessage } from "../../api/client";
import { LYRIC_SIZE_LABELS, LYRIC_SIZES, isDarkTheme } from "../../lib/preferences";
import { getHostKey, hostKeyLink } from "../../session/hostKey";
import { copyText } from "../../lib/clipboard";
import { selectIsHost } from "../../selectors";

const ROTATE_WARNING =
  "Replace your host key? The current key stops working at once on every device and in every host link that has it. Your sessions and songs stay yours.";

/** Per-device display settings, sound sync, the host key with its QR handoff, and the way out. */
const SettingsPage = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const preferences = useSelector((state) => state.preferences);
  const soundSync = useSelector((state) => state.soundSync);
  const sessionCode = useSelector((state) => state.session.Code);
  const isHost = useSelector(selectIsHost);
  const wakeLockSupported = typeof navigator !== "undefined" && Boolean(navigator.wakeLock);
  const [hostKey, setStoredKey] = useState(getHostKey);
  const [rotating, setRotating] = useState(false);
  const [showQr, setShowQr] = useState(false);
  const [copied, setCopied] = useState(null);

  const copy = async (text, what) => {
    if (await copyText(text)) {
      setCopied(what);
      setTimeout(() => setCopied(null), 2000);
    }
  };

  // For a key that was shared with the wrong person: a fresh key, the old one revoked everywhere.
  const rotate = async () => {
    if (rotating || !window.confirm(ROTATE_WARNING)) return;
    setRotating(true);
    try {
      setStoredKey(await dispatch(rotateHostKey()));
      setShowQr(false);
      dispatch(showNotice("You have a new host key", "info", { sub: "The old one stopped working everywhere" }));
    } catch (error) {
      dispatch(showNotice(failureMessage(error, "Couldn't replace the host key. Check your connection and try again.")));
    } finally {
      setRotating(false);
    }
  };

  const leave = () => {
    dispatch(clearSession());
    navigate("/", { replace: true });
  };

  const dark = isDarkTheme(preferences.theme);
  const soundSub = soundSync.enabled
    ? soundSync.status === "interrupted"
      ? "Interrupted; the banner offers to resume"
      : soundSync.role === "host"
        ? "Broadcasting to nearby phones"
        : "Listening for the host"
    : soundSync.status === "starting"
      ? "Starting…"
      : "Keep the room together with no signal";

  return (
    <div className="page" data-screen-label="Display">
      <div className="between">
        <h2 className="page-title">Display</h2>
        <button type="button" className="back-link" onClick={() => navigate(-1)}>
          Done
        </button>
      </div>
      <div className="card">
        <div className="between" style={{ marginBottom: 14 }}>
          <span className="card-title">Lyric size</span>
          <span className="small muted" style={{ fontWeight: 700 }}>
            {LYRIC_SIZE_LABELS[preferences.lyricSize]}
          </span>
        </div>
        <div className="size-steps" role="radiogroup" aria-label="Lyric size">
          {LYRIC_SIZES.map((_, index) => (
            <button key={index} type="button" className="size-step" role="radio" aria-checked={preferences.lyricSize === index} aria-label={LYRIC_SIZE_LABELS[index]} style={{ fontSize: 12 + index * 4 }} onClick={() => dispatch(setLyricSize(index))}>
              A
            </button>
          ))}
        </div>
      </div>
      <Toggle label="Dim room" sub="Dark background, warm ink" on={dark} onChange={() => dispatch(toggleDimRoom())} />
      <Toggle label="High contrast" sub="Pure black text, heavier weight" on={preferences.highContrast} onChange={(on) => dispatch(setHighContrast(on))} />
      <Toggle label="Chords above lyrics" sub="Only where the song has them" on={preferences.showChords} onChange={(on) => dispatch(setShowChords(on))} />
      <Toggle
        label="Keep screen on"
        sub={wakeLockSupported ? "While lyrics are showing" : "Not supported on this device"}
        on={preferences.keepAwake && wakeLockSupported}
        onChange={(on) => dispatch(setKeepAwake(on))}
        disabled={!wakeLockSupported}
      />
      <Toggle label="Sound sync" sub={soundSub} on={soundSync.enabled} onChange={(on) => dispatch(on ? enableSoundSync() : disableSoundSync())} disabled={soundSync.status === "starting" || !sessionCode} />
      <Toggle label="Keep favorites offline" sub="Sings through a dead signal" on={preferences.keepFavoritesOffline} onChange={(on) => dispatch(setKeepFavoritesOffline(on))} />

      {hostKey && (
        <div className="card" data-screen-label="Host key">
          <div className="between" style={{ alignItems: "baseline", marginBottom: 8 }}>
            <span className="card-title">Host key</span>
            <span className="small muted">Runs every session you host</span>
          </div>
          <div className="mono">{hostKey}</div>
          <div className="wrap" style={{ marginTop: 12 }}>
            <button type="button" className="btn btn-xs btn-outline" onClick={() => copy(hostKey, "key")}>
              {copied === "key" ? "Copied!" : "Copy host key"}
            </button>
            <button type="button" className="btn btn-xs btn-outline" onClick={() => copy(hostKeyLink(hostKey, sessionCode), "link")}>
              {copied === "link" ? "Copied!" : "Copy host link"}
            </button>
            <button type="button" className="btn btn-xs btn-outline" aria-pressed={showQr} onClick={() => setShowQr((open) => !open)}>
              {showQr ? "Hide QR" : "Show QR for handoff"}
            </button>
            <button type="button" className="btn btn-xs btn-danger" onClick={rotate} disabled={rotating} aria-label="Replace the host key">
              {rotating ? "Replacing…" : "New key…"}
            </button>
          </div>
          {showQr && (
            <div className="qr-card" style={{ marginTop: 14, padding: 14 }}>
              <QrCode value={hostKeyLink(hostKey, sessionCode)} size={200} className="qr-img qr-img-sm" alt="QR code with the host link" />
              <div className="grow">
                <div className="card-title">Hand off to another phone</div>
                <div className="row-sub" style={{ whiteSpace: "normal", color: "var(--accent-muted)" }}>
                  Scan on the co-host’s phone. It opens this session as host with your songs and sets. Guests are not interrupted.
                </div>
              </div>
            </div>
          )}
          <p className="note" style={{ marginTop: 10 }}>
            A new key stops the old one at once on every device and in every host link that has it. Sessions, favorites and songs stay yours.
          </p>
        </div>
      )}

      {isHost && (
        <button type="button" className="toggle-row" onClick={() => navigate("/issues")}>
          <span className="grow">
            <span className="toggle-label">Reported issues</span>
            <span className="toggle-sub">Problems people reported with songs</span>
          </span>
          <span aria-hidden="true">→</span>
        </button>
      )}

      {sessionCode && (
        <button type="button" className="btn btn-md btn-outline" onClick={leave}>
          Leave session {sessionCode}
        </button>
      )}
    </div>
  );
};

export default SettingsPage;
